/**
 * Day Book (fee collection + misc income − expense, per day and per mode) and
 * Category Summary (misc income / expense per category).
 */
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Row, Col, Card, Table, Button, Form, Nav, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import schoolApi from "../../api/schoolApi";
import { SessionContext } from "../SessionProvider";
import { inr, fmtDate, monthStart, today, userInfo } from "./ledgerUtils";

const downloadCsv = (name, rows) => {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

const Stat = ({ label, value, color }) => (
  <Col md={3} xs={6}>
    <Card className="shadow-sm h-100" style={{ borderLeft: `4px solid ${color}` }}>
      <Card.Body className="py-2">
        <div className="small text-muted">{label}</div>
        <div className="fs-5 fw-bold" style={{ color }}>₹ {inr(value)}</div>
      </Card.Body>
    </Card>
  </Col>
);

const LedgerReports = ({ tabName }) => {
  const { selectedSessionId } = useContext(SessionContext);
  const [tab, setTab] = useState("daybook");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [wholeSession, setWholeSession] = useState(false);
  const [day, setDay] = useState(null);
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();
  const u = userInfo();

  const handlePrint = useReactToPrint({ content: () => printRef.current, documentTitle: tab === "daybook" ? "DayBook" : "CategorySummary" });

  const load = useCallback(async () => {
    if (!from || !to) return;
    if (from > to) return toast.warn("From date is after To date");
    setLoading(true);
    try {
      if (tab === "daybook") {
        const r = await schoolApi.ledgerGet("/reports/daybook", { from, to, session_id: selectedSessionId });
        setDay(r?.result || null);
      } else {
        const params = wholeSession ? { session_id: selectedSessionId } : { from, to, session_id: selectedSessionId };
        const r = await schoolApi.ledgerGet("/reports/category-summary", params);
        setSum(r?.result || null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not load report");
    } finally {
      setLoading(false);
    }
  }, [tab, from, to, wholeSession, selectedSessionId]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (tab === "daybook" && day) {
      downloadCsv(`daybook_${from}_${to}.csv`, [
        ["Date", "Fee Collection", "Misc Income", "Expense", "Net"],
        ...day.days.map((d) => [fmtDate(d.date), d.fee, d.income, d.expense, d.net]),
        ["Total", day.total.fee, day.total.income, day.total.expense, day.total.net],
        [],
        ["Mode", "Fee Collection", "Misc Income", "Expense", "Net"],
        ...day.modes.map((m) => [m.mode, m.fee, m.income, m.expense, m.net]),
      ]);
    } else if (sum) {
      downloadCsv(`category_summary.csv`, [
        ["Type", "Category", "Entries", "Amount"],
        ...sum.rows.map((r) => [r.type, r.name, r.entries, r.amount]),
        [],
        ["", "Total Income", "", sum.income],
        ["", "Total Expense", "", sum.expense],
        ["", "Net", "", sum.net],
      ]);
    }
  };

  const period = tab === "summary" && wholeSession ? "Whole session" : `${fmtDate(from)} to ${fmtDate(to)}`;

  return (
    <>
      <Helmet><title>{tabName || "Ledger Reports"}</title></Helmet>
      <Row className="g-0 px-lg-4 px-2">
        <Col lg={12} className="mt-3 mb-2">
          <Link className="nav-link" to="/">
            Home <i className="fa-solid fa-chevron-right" /> Accounts <i className="fa-solid fa-chevron-right" /> Reports
          </Link>
        </Col>
        <Col lg={12}>
          <Card className="shadow-sm mb-3">
            <Card.Header className="bg-white">
              <Nav variant="tabs" activeKey={tab} onSelect={(k) => setTab(k)}>
                <Nav.Item><Nav.Link eventKey="daybook">Day Book</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="summary">Category Summary</Nav.Link></Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body className="py-2">
              <Row className="g-2 align-items-end">
                <Col md={2}>
                  <Form.Label className="small mb-0">From</Form.Label>
                  <Form.Control size="sm" type="date" value={from} disabled={tab === "summary" && wholeSession}
                    onChange={(e) => setFrom(e.target.value)} />
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-0">To</Form.Label>
                  <Form.Control size="sm" type="date" value={to} disabled={tab === "summary" && wholeSession}
                    onChange={(e) => setTo(e.target.value)} />
                </Col>
                {tab === "summary" && (
                  <Col md={3}>
                    <Form.Check type="switch" id="whole" label="Whole session" checked={wholeSession}
                      onChange={(e) => setWholeSession(e.target.checked)} />
                  </Col>
                )}
                <Col className="text-end">
                  <Button size="sm" variant="outline-secondary" onClick={exportCsv} disabled={loading}>
                    <i className="fa-solid fa-file-csv me-1" /> Excel (CSV)
                  </Button>{" "}
                  <Button size="sm" variant="outline-primary" onClick={handlePrint} disabled={loading}>
                    <i className="fa-solid fa-print me-1" /> Print
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={12}>
          {loading && <div className="text-center py-5"><Spinner animation="border" /></div>}
          {!loading && (
            <div ref={printRef} className="p-1">
              <div className="d-none d-print-block text-center mb-2">
                <h4 className="mb-0">{u.companyname}</h4>
                <div>{tab === "daybook" ? "Day Book" : "Income / Expense – Category Summary"} · {period}</div>
              </div>

              {tab === "daybook" && day && (
                <>
                  <Row className="g-2 mb-3">
                    <Stat label="Fee collection" value={day.total.fee} color="#0d6efd" />
                    <Stat label="Misc income" value={day.total.income} color="#198754" />
                    <Stat label="Expense" value={day.total.expense} color="#dc3545" />
                    <Stat label="Net (cash in hand change)" value={day.total.net} color="#212529" />
                  </Row>
                  <Row className="g-3">
                    <Col lg={8}>
                      <Card className="shadow-sm">
                        <Card.Header className="bg-white fw-semibold">Day wise</Card.Header>
                        <Table size="sm" responsive hover className="mb-0">
                          <thead className="table-light">
                            <tr><th>Date</th><th className="text-end">Fee</th><th className="text-end">Misc Income</th><th className="text-end">Expense</th><th className="text-end">Net</th></tr>
                          </thead>
                          <tbody>
                            {day.days.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-3">Nothing in this period</td></tr>}
                            {day.days.map((d) => (
                              <tr key={d.date}>
                                <td>{fmtDate(d.date)}</td>
                                <td className="text-end">{inr(d.fee)}</td>
                                <td className="text-end text-success">{inr(d.income)}</td>
                                <td className="text-end text-danger">{inr(d.expense)}</td>
                                <td className="text-end fw-semibold">{inr(d.net)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="table-light fw-bold">
                            <tr><td>Total</td><td className="text-end">{inr(day.total.fee)}</td><td className="text-end">{inr(day.total.income)}</td>
                              <td className="text-end">{inr(day.total.expense)}</td><td className="text-end">{inr(day.total.net)}</td></tr>
                          </tfoot>
                        </Table>
                      </Card>
                    </Col>
                    <Col lg={4}>
                      <Card className="shadow-sm">
                        <Card.Header className="bg-white fw-semibold">Payment mode wise</Card.Header>
                        <Table size="sm" className="mb-0">
                          <thead className="table-light"><tr><th>Mode</th><th className="text-end">In</th><th className="text-end">Out</th><th className="text-end">Net</th></tr></thead>
                          <tbody>
                            {day.modes.map((m) => (
                              <tr key={m.mode}>
                                <td>{m.mode}</td>
                                <td className="text-end">{inr(m.fee + m.income)}</td>
                                <td className="text-end text-danger">{inr(m.expense)}</td>
                                <td className="text-end fw-semibold">{inr(m.net)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card>
                    </Col>
                  </Row>
                </>
              )}

              {tab === "summary" && sum && (
                <>
                  <Row className="g-2 mb-3">
                    <Stat label="Misc income" value={sum.income} color="#198754" />
                    <Stat label="Expense" value={sum.expense} color="#dc3545" />
                    <Stat label="Net" value={sum.net} color="#212529" />
                  </Row>
                  <Row className="g-3">
                    {["INCOME", "EXPENSE"].map((t) => {
                      const list = sum.rows.filter((r) => r.type === t);
                      const total = list.reduce((a, r) => a + r.amount, 0);
                      return (
                        <Col lg={6} key={t}>
                          <Card className="shadow-sm">
                            <Card.Header className="bg-white fw-semibold" style={{ color: t === "INCOME" ? "#198754" : "#dc3545" }}>
                              {t === "INCOME" ? "Income" : "Expense"} by category
                            </Card.Header>
                            <Table size="sm" className="mb-0">
                              <thead className="table-light"><tr><th>Category</th><th className="text-end">Entries</th><th className="text-end">Amount</th><th style={{ width: "30%" }}></th></tr></thead>
                              <tbody>
                                {list.length === 0 && <tr><td colSpan={4} className="text-center text-muted py-3">Nothing</td></tr>}
                                {list.map((r) => (
                                  <tr key={r.id}>
                                    <td>{r.name}</td>
                                    <td className="text-end">{r.entries}</td>
                                    <td className="text-end fw-semibold">{inr(r.amount)}</td>
                                    <td>
                                      <div style={{ background: "#eef0f2", borderRadius: 3, height: 8 }}>
                                        <div style={{ width: `${total ? (r.amount / total) * 100 : 0}%`, height: 8, borderRadius: 3,
                                          background: t === "INCOME" ? "#198754" : "#dc3545" }} />
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="table-light fw-bold"><tr><td>Total</td><td></td><td className="text-end">{inr(total)}</td><td></td></tr></tfoot>
                            </Table>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </>
              )}
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

export default LedgerReports;
